import { ReactNode, createContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { IAuthenticationContextType } from "../interfaces/IAuthenticationContextType";
import { useErrorContext } from "../../global/hooks/useErrorContext";

//The authentication context object holds the latest IAuthenticationContextType.
export const AuthenticationContext = createContext<IAuthenticationContextType | undefined>(undefined);

//This component is responsible to manage the token state and provide it through the application component tree.
export const AuthenticationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(null);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [loadingAuthenCtx, setLoadingAuthenCtx] = useState<boolean>(true);
    const [cstKey, setCustomerKey] = useState<string | undefined>(undefined);
    const [queryString, setQueryString] = useState<string | null>(null);
    const { setError } = useErrorContext();
    const AUTHENTICATION_URI = process.env.REACT_APP_AUTHENTICATION_CONTROLLER;

    /**
     * Local storage contract for authentication token persistence.
     *
     * TOKEN_STORAGE_KEY stores the token string and TOKEN_EXPIRY_STORAGE_KEY stores
     * the absolute expiration time (epoch milliseconds). Tokens are considered
     * expired after 20 minutes and are removed before auth flow continues.
     */
    const TOKEN_STORAGE_KEY = "NfToken";
    const TOKEN_EXPIRY_STORAGE_KEY = "NfTokenExpiry";
    const TOKEN_TTL_MS = 20 * 60 * 1000;

    /**
     * Persists token and expiration together.
     *
     * Expiration uses an absolute timestamp so validation remains consistent
     * across refreshes and browser restarts.
     */
    const setTokenWithExpiry = (tokenValue: string) => {
        const expiresAt = Date.now() + TOKEN_TTL_MS;
        localStorage.setItem(TOKEN_STORAGE_KEY, tokenValue);
        localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiresAt.toString());
    };

    /**
     * Removes both token value and expiration metadata.
     */
    const clearStoredToken = () => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
    };

    /**
     * Returns a token only when still valid.
     *
     * Behavior:
     * - Returns null when no token exists.
     * - Clears storage and returns null when expiry metadata is missing.
     * - Clears storage and returns null when token is expired.
     */
    const getValidStoredToken = () => {
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!storedToken) {
            return null;
        }

        const expiresAt = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);

        // Tokens missing expiry metadata are considered stale and invalid.
        // This bypasses legacy tokens that were stored without expiration.
        if (!expiresAt) {
            clearStoredToken();
            return null;
        }

        if (Date.now() > Number(expiresAt)) {
            clearStoredToken();
            return null;
        }

        return storedToken;
    };

    const currentUrl = new URL(window.location.href);
    const redirectUrl = window.location.href;
    const originUrl = window.location.origin;
    let aapHost = originUrl;

    if (originUrl.includes("features.aap.org")) {
        aapHost = originUrl.replace("features.", "");
    }
    else {
        aapHost = originUrl.replace("features", "aap");
    }

    const AAP_AUTH_RETRY_URL = process.env.REACT_APP_AAP_RETRY_AUTHENTICATION_URL;
    const retryAuthenticationPageUrl = `${aapHost}${AAP_AUTH_RETRY_URL}${redirectUrl}`;
    
    const handleError = (message: string | null, statusCode: number) => {
        setError({ message, statusCode });
        setLoadingAuthenCtx(false);
    };

    /**
     * AuthenticationControllerFetch
     *
     * Fetches and validates authentication data from the authentication API endpoint using the provided token.
     *
     * @param authToken - The authentication token (nftoken) to be validated.
     * @returns Promise<boolean> - Resolves to `true` if the user needs to log in (e.g., token is invalid or expired, 401 response),
     *                             or `false` if authentication is successful.
     *
     * Behavior:
     * - Sends a GET request to the authentication API with the provided token.
     * - If the response status is 401 (unauthorized), resolves with `true` (indicating login is required).
     * - If the response is OK, parses the result and updates the authentication state (token and customer key).
     * - If the result is missing or invalid, triggers the error handler.
     * - Handles errors by invoking the error context and updating the loading state.
     * - Always updates the loading state before and after the fetch operation.
     *
     * Side Effects:
     * - Updates authentication state (token, customer key).
     * - Updates loading state.
     * - Triggers error handling via the error context if needed.
     */
    const AuthenticationControllerFetch = async (authToken: string) => {
        return new Promise((resolve) => { (async () => {
        const apiAuthentication = `${AUTHENTICATION_URI}?nftoken=${authToken}`;
        try {
            setLoadingAuthenCtx(true);
            const response = await fetch(apiAuthentication);
            if (response.status === 401) {
                resolve(true);
                return;
            }
            if (response.ok) {
                const result = await response.json();
                if (!result) {
                    return handleError(null, 1);
                }
                else {
                    resolve(false);
                    setToken(result.NfToken);
                    setCustomerKey(result.CustomerKey);
                }
            }
        }
        catch (error: any) {
            handleError(null, error.status);
        }
        finally {
            setLoadingAuthenCtx(false);
        }
        })();
        });
    };

    /**
 * useEffect - Handles authentication state initialization and redirection logic.
 *
 * This effect runs once on component mount. It checks for an authentication token
 * in the URL query string (`nftoken`) or in localStorage. Local storage tokens are
 * validated against a 20-minute expiration window before use. If a token is found, it:
 *   - Sets the token state and (if present) stores the current query string.
 *   - Calls AuthenticationControllerFetch to validate the token.
 *     - If the token is invalid or expired (401), it:
 *         - Resets authentication state and localStorage.
 *         - Redirects the user to the /accessdenied page.
 *     - If the token is valid, it:
 *         - Stores the token in localStorage.
 *         - Sets the user as logged in.
 *         - Cleans up the URL by removing the query string.
 * If no token is found, it:
 *   - Resets authentication state and localStorage.
 *   - Redirects the user to the login page.
 *
 * Side Effects:
 * - Updates authentication-related state and localStorage.
 * - Performs client-side redirects based on authentication status.
 *
 * Dependencies: []
 */
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('nftoken');
        const storageToken = getValidStoredToken();
        const nfToken = urlToken ?? storageToken;

        if (nfToken) {
            setToken(nfToken);
            if (window.location.search != "") {
                setQueryString(window.location.search);
                localStorage.setItem("querystring", window.location.search);
            }
            AuthenticationControllerFetch(nfToken).then((needsLogin) => {
                if (needsLogin) {
                    setIsLoggedIn(false);
                    currentUrl.searchParams.delete('nftoken');
                    setToken(null);
                    setQueryString(null);
                    clearStoredToken();
                    localStorage.removeItem("querystring");
                    window.location.href = retryAuthenticationPageUrl;
                } else {
                    setTokenWithExpiry(nfToken);
                    setIsLoggedIn(true);
                    window.history.replaceState({}, document.title, window.location.pathname);
                }
            });
            } else {
            setIsLoggedIn(false);
            setQueryString(null);
            clearStoredToken();
            localStorage.removeItem("querystring");
            window.location.href = retryAuthenticationPageUrl;
        }
    }, []);

    const value = {
        token,
        cstKey,
        queryString,
        setToken,
        isLoggedIn
    }

    return (
        <AuthenticationContext.Provider value={value}>
            { loadingAuthenCtx ? ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 1, color:"primary.dark" }}><CircularProgress disableShrink size={25} /></Box> ) : ( value.isLoggedIn ? children : null )} 
        </AuthenticationContext.Provider>
    );
}